import { NavLink, Route, Routes, useParams } from "react-router-dom";
import { api, t } from "../lib/api";
import { useAsync } from "../lib/hooks";
import { ErrorNote } from "../components/ui";
import { DashboardTab } from "./engagement/Dashboard";
import { InterviewsTab } from "./engagement/Interviews";
import { PlanTab } from "./engagement/Plan";
import { GuideTab } from "./engagement/Guide";
import { GlossaryTab } from "./engagement/Glossary";
import { SettingsTab } from "./engagement/Settings";

export interface Engagement {
  id: string;
  name: string;
  clientUnit: string | null;
  country: string;
  phase: string;
  interviewerLanguages: string[];
  intervieweeLanguages: string[];
  reportLanguage: string;
  anonymiseNames: boolean;
  focusModules: string[];
  targetInterviews: number | null;
  publishedToClient: boolean;
  memberIds: string[];
  role: string;
}

export function EngagementPage() {
  const { tenant, eid } = useParams();
  const eng = useAsync(() => api.get<Engagement>(`${t(tenant!)}/engagements/${eid}`), [tenant, eid]);
  if (eng.error) return <ErrorNote error={eng.error} />;
  if (!eng.data) return <p className="muted">Loading…</p>;
  const e = eng.data;
  const client = e.role === "client_viewer";
  const lead = e.role === "admin" || e.role === "lead";
  const base = `/w/${tenant}/e/${eid}`;
  return (
    <>
      <div className="page-head">
        <div>
          <h1>{e.name}</h1>
          <p className="sub">
            {e.clientUnit ? `${e.clientUnit} · ` : ""}
            {e.country} · {e.interviewerLanguages.join(", ")} ↔ {e.intervieweeLanguages.join(", ")}
          </p>
        </div>
        <span className="spacer" />
        {!client && (
          <span className={`pill ${e.publishedToClient ? "green" : ""}`}>{e.publishedToClient ? "Shared with client" : "Not shared with client"}</span>
        )}
      </div>
      {!client && (
        <nav className="tabs" aria-label="Engagement">
          <NavLink to={base} end>
            Dashboard
          </NavLink>
          <NavLink to={`${base}/interviews`}>Interviews</NavLink>
          <NavLink to={`${base}/plan`}>Plan and people</NavLink>
          <NavLink to={`${base}/guide`}>Question guide</NavLink>
          <NavLink to={`${base}/glossary`}>Glossary</NavLink>
          {lead && <NavLink to={`${base}/settings`}>Settings</NavLink>}
        </nav>
      )}
      <Routes>
        <Route index element={<DashboardTab engagement={e} reload={eng.reload} />} />
        {!client && <Route path="interviews" element={<InterviewsTab engagement={e} />} />}
        {!client && <Route path="plan" element={<PlanTab engagement={e} />} />}
        {!client && <Route path="guide" element={<GuideTab engagement={e} />} />}
        {!client && <Route path="glossary" element={<GlossaryTab engagement={e} />} />}
        {lead && <Route path="settings" element={<SettingsTab engagement={e} onSaved={eng.reload} />} />}
      </Routes>
    </>
  );
}
